import { useState, useEffect } from 'react';
import { useEntity } from '../hooks/useEntity';
import { resourceEntityConfig, ResourceType } from '../entities/Resource';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ResourcesPage() {
  const navigate = useNavigate();
  const { items: resources, loading, create, update, delete: deleteResource, reload } = useEntity<ResourceType>(resourceEntityConfig);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ResourceType | null>(null);
  const [formData, setFormData] = useState({
    serviceName: '',
    appName: '',
    cost: '',
    billingCycle: 'monthly' as 'monthly' | 'yearly' | 'one-time',
    startDate: '',
    renewalDate: '',
    status: 'active' as 'active' | 'inactive' | 'trial',
    notes: ''
  });

  useEffect(() => {
    reload();
  }, [reload]);

  const getDaysUntilRenewal = (renewalDate: string) => {
    const today = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = renewal.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRenewalAlert = (days: number) => {
    if (days < 0) return { color: 'text-red-600', message: 'OVERDUE!' };
    if (days === 0) return { color: 'text-red-600', message: 'Due TODAY' };
    if (days <= 3) return { color: 'text-orange-600', message: `${days} days` };
    if (days <= 7) return { color: 'text-yellow-600', message: `${days} days` };
    return { color: 'text-gray-600', message: `${days} days` };
  };

  const getTotalMonthlyCost = () => {
    return resources
      .filter(r => r.status === 'active')
      .reduce((total, r) => {
        if (r.billingCycle === 'yearly') return total + (r.cost / 12);
        if (r.billingCycle === 'monthly') return total + r.cost;
        return total;
      }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...formData,
      cost: parseFloat(formData.cost)
    };

    if (editingResource) {
      await update(editingResource.id, data);
    } else {
      await create(data);
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      serviceName: '',
      appName: '',
      cost: '',
      billingCycle: 'monthly',
      startDate: '',
      renewalDate: '',
      status: 'active',
      notes: ''
    });
    setEditingResource(null);
  };

  const handleEdit = (resource: ResourceType) => {
    setEditingResource(resource);
    setFormData({
      serviceName: resource.serviceName,
      appName: resource.appName,
      cost: resource.cost.toString(),
      billingCycle: resource.billingCycle,
      startDate: resource.startDate,
      renewalDate: resource.renewalDate,
      status: resource.status,
      notes: resource.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this resource?')) {
      await deleteResource(id);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-white text-xl">Loading resources...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header - Subtle Orange Gradient Bar */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Resource Tracker</h1>
              <p className="text-sm text-amber-100">Manage Subscriptions & Services</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm} 
                className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold transition-colors border-0"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-slate-800 border border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white">{editingResource ? 'Edit Resource' : 'Add New Resource'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Service Name</label>
                    <Input
                      value={formData.serviceName}
                      onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                      placeholder="e.g., Turso, Supabase"
                      className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">App Name</label>
                    <Input
                      value={formData.appName}
                      onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
                      placeholder="e.g., WHIPT, BBQ Community"
                      className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Cost ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      placeholder="0.00"
                      className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Billing Cycle</label>
                    <Select value={formData.billingCycle} onValueChange={(value: any) => setFormData({ ...formData, billingCycle: value })}>
                      <SelectTrigger className="bg-slate-900 text-white border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="one-time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Start Date</label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Renewal Date</label>
                    <Input
                      type="date"
                      value={formData.renewalDate}
                      onChange={(e) => setFormData({ ...formData, renewalDate: e.target.value })}
                      className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Status</label>
                  <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="bg-slate-900 text-white border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Notes (optional)</label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional details..."
                    className="bg-slate-900 text-white border-slate-700 focus:border-rose-500"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-slate-700 text-white hover:bg-slate-700">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-rose-500 to-amber-500 hover:shadow-lg hover:shadow-rose-500/50 text-white border-0">
                    {editingResource ? 'Update' : 'Add'} Resource
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards - Match admin dashboard style with different colors */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {/* Ruby Red Card */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
                <Plus className="w-6 h-6 text-rose-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{resources.filter(r => r.status === 'active').length}</p>
                <p className="text-sm text-slate-400">Active Resources</p>
              </div>
            </div>
          </div>
          
          {/* Amber Gold Card */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-xl text-amber-400">$</span>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">${getTotalMonthlyCost().toFixed(2)}</p>
                <p className="text-sm text-slate-400">Monthly Cost</p>
              </div>
            </div>
          </div>
          
          {/* Orange Sunset Card */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <span className="text-xl text-orange-400">$</span>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">${(getTotalMonthlyCost() * 12).toFixed(2)}</p>
                <p className="text-sm text-slate-400">Yearly Estimate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Table - Match admin dashboard style */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Service</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">App</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Cost</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Cycle</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Renewal</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {resources.map((resource) => {
                const daysUntil = getDaysUntilRenewal(resource.renewalDate);
                const alert = getRenewalAlert(daysUntil);
                return (
                  <tr key={resource.id} className="hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-white">{resource.serviceName}</div>
                      {resource.notes && <div className="text-sm text-slate-400">{resource.notes}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">{resource.appName}</td>
                    <td className="px-6 py-4 text-sm font-bold text-white">${resource.cost.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 capitalize">{resource.billingCycle}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white font-medium">{new Date(resource.renewalDate).toLocaleDateString()}</div>
                      <div className={`text-xs font-bold flex items-center gap-1 ${alert.color}`}>
                        {daysUntil <= 7 && <AlertCircle className="h-3 w-3" />}
                        {alert.message}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-bold rounded-lg ${
                        resource.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        resource.status === 'trial' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-slate-600/50 text-slate-400'
                      }`}>
                        {resource.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEdit(resource)} 
                          className="hover:bg-slate-700 text-slate-300 hover:text-white"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDelete(resource.id)} 
                          className="hover:bg-red-500/20 text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {resources.length === 0 && (
            <div className="text-center py-20 text-slate-400">
              <div className="text-xl font-bold mb-2">No resources tracked yet</div>
              <div className="text-sm">Click "Add Resource" to start tracking your subscriptions!</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
